import { notFound } from "next/navigation";

import type { BlogData, BlogListData } from "./types";
import { OpeningsJson } from "./openingsClient";
import { resolveOpeningsBusiness } from "./openingsBusiness";
import type {
  OpeningsBlogPostDetail,
  OpeningsBlogPostListItem,
} from "./openingsTypes";

const BLOG_POSTS_LIMIT = 50;
const MAX_BLOG_PAGES = 100;

const asDate = (value: string | null | undefined) =>
  value ? new Date(value) : new Date(0);

const normalizeBlogContent = (content: string) =>
  content.replace(/&(nbsp|#160|#xA0);|\u00a0/gi, " ");

const mapBlogListItem = (post: OpeningsBlogPostListItem): BlogListData => ({
  _id: post.id,
  title: post.title,
  coverImage: post.coverImageUrl ?? "",
  path: post.slug,
  excerpt: post.excerpt?.trim() || undefined,
  createdAt: asDate(post.publishedAt ?? post.createdAt),
  updatedAt: asDate(post.updatedAt),
});

const mapBlogDetail = (post: OpeningsBlogPostDetail): BlogData => ({
  ...mapBlogListItem(post),
  content: normalizeBlogContent(post.content),
  ...(post.seoTitle?.trim() ? { seoTitle: post.seoTitle.trim() } : {}),
  ...(post.seoDescription?.trim()
    ? { seoDescription: post.seoDescription.trim() }
    : {}),
});

export const getOpeningsBackedBlogArticles = async (): Promise<
  BlogListData[]
> => {
  const business = await resolveOpeningsBusiness("eddies");
  const posts: OpeningsBlogPostListItem[] = [];
  const seenCursors = new Set<string>();
  let cursor: string | undefined;

  for (let page = 0; page < MAX_BLOG_PAGES; page += 1) {
    const response = await OpeningsJson.getBlogPosts({
      businessId: business.id,
      limit: BLOG_POSTS_LIMIT,
      ...(cursor ? { cursor } : {}),
    });
    posts.push(...response.items);

    const nextCursor = response.nextCursor || undefined;
    if (!nextCursor || seenCursors.has(nextCursor)) break;
    seenCursors.add(nextCursor);
    cursor = nextCursor;

    if (page === MAX_BLOG_PAGES - 1) {
      console.warn(
        `[openings] blog pagination exceeded ${MAX_BLOG_PAGES} pages; stopping defensively`,
      );
    }
  }

  return posts.map(mapBlogListItem);
};

export const getOpeningsBackedBlogArticle = async (
  articlePath: string,
): Promise<BlogData> => {
  const business = await resolveOpeningsBusiness("eddies");
  try {
    const response = await OpeningsJson.getBlogPost({
      businessId: business.id,
      slug: articlePath,
    });
    return mapBlogDetail(response.post);
  } catch (error) {
    if ((error as { status?: number }).status === 404) {
      notFound();
    }
    throw error;
  }
};
